import { useMemo } from 'react'
import { useTranslation } from 'next-i18next'

import { isGroup } from '@/utils/listings'
import { ListingCardProps } from '@/components/listing-card-new/ListingCard.types'
import { Listing } from '@/components/listing-card-new/types'
import {
  formatBedsBaths,
  formatPrice,
  getBuildingBedsBaths,
  getBuildingPrice
} from '@/components/listing-card-new/utils/formatDetails'
import { getListingTypeText } from '@/components/listing-card-new/utils/listingTypeText'
import { getListingUrl } from '@/components/listing-card-new/utils/listingUrl'
import { getIsRented, getListingCardDlv } from '@/components/listing-card/utils'

interface UseListingCardDataOptions {
  isFeatured?: boolean
  hasSession?: boolean
  isAgreementMismatched?: boolean
  isAgreementLoading?: boolean
}

interface ListingWithBuilding {
  building_id: string
  listing_count: number
  listings: Listing[]
  city: string
  state: string
  full_street_name: string
  building_name?: string
  units_available: number
  building_type?: string
  listing_id?: string
  state_machine?: string
  marked_as_rented?: boolean
  building_files?: { aws_s3_key: string; tag?: string }[]
}

// Type for individual flat listings (featured listings)
interface IndividualListing extends Listing {
  city: string
  state: string
  full_street_name: string
  building_name?: string
  search_deals?: string[]
  previous_price?: number | null
  price_change_status?: string | null
}

type ListingData = ListingWithBuilding | IndividualListing

const getCover = (images: { aws_s3_key: string; tag?: string }[]) => {
  if (!images?.length) return false
  const cover = images.find(({ tag }) => tag === 'cover_photo')?.aws_s3_key
  return cover ? [cover] : [images[0].aws_s3_key]
}

const getBuildingImages = (listing: ListingData) => {
  if (
    'building_files' in listing &&
    listing.building_files &&
    listing.building_files.length > 0
  ) {
    return getCover(listing.building_files)
  }
  return null
}

const getUnitImages = (listings: Listing[]) => {
  return listings.flatMap(l => {
    if (l.unit_files) {
      return [...(l.unit_files || [])]
        .sort(a => (a.tag === 'cover_photo' ? -1 : 1))
        .map((image: { aws_s3_key: string }) => image.aws_s3_key)
    } else if (l.cover_photo_aws_s3_key) {
      return [l.cover_photo_aws_s3_key]
    }
    return []
  })
}

const getListingImages = (
  listing: ListingData,
  listings: Listing[],
  variant: ListingCardProps['variant']
) => {
  if (variant === 'building' || variant === 'multiUnit') {
    const buildingImages = getBuildingImages(listing)
    if (buildingImages) {
      return buildingImages
    }
  }

  return getUnitImages(listings)
}

const isStreetNumberHidden = (listing: any) => {
  return (
    !!Number(listing.hide_unit_number) && listing.unit_type_txt_id === 'HOUSE'
  )
}

export const useListingCardData = (
  listing: ListingData,
  options: UseListingCardDataOptions = {}
) => {
  const { t } = useTranslation()
  const {
    isFeatured = false,
    hasSession = false,
    isAgreementMismatched = false,
    isAgreementLoading = false
  } = options

  return useMemo(() => {
    if (!listing) return null

    const listingCardDlv = getListingCardDlv(listing as any, isFeatured)

    let listings: Listing[]
    let listing_count: number = 1
    let units_available: number

    // Check if this is building data (has listings array) or individual listing data
    // For example, featured listings come as flat listings while regular ones come
    // as buildings with listings arrays
    if ('listings' in listing && Array.isArray(listing.listings)) {
      const buildingData = listing as ListingWithBuilding
      listings = buildingData.listings

      if (listings.length === 1 && listings.some(isGroup)) {
        units_available = parseInt(listings[0]?.gr_count ?? '1')
      } else {
        listing_count = buildingData.listing_count || listings?.length
        units_available = Math.max(listing_count, 1)
      }
    } else {
      const _listing = listing as Listing
      listings = [_listing]
      units_available = Math.max(parseInt(_listing?.gr_count ?? '1'), 1)
    }

    if (!listings || !Array.isArray(listings) || listings.length === 0) {
      return null
    }

    const isBuilding = listing_count > 1 || listings.length > 1
    const isPublicViewing = listings[0]?.public_viewing === false
    const isCardLocked =
      isPublicViewing &&
      (isAgreementMismatched || !hasSession || isAgreementLoading)
    const isRented = getIsRented(listings[0])
    const isMultiUnit = listings.some(isGroup)

    let specialOffer = false

    let variant: ListingCardProps['variant'] = 'default'
    if (isFeatured) {
      variant = 'sponsored'
    } else if (isBuilding) {
      const isVariousRates = listings.some(
        listing => listing.price_frequency !== listings[0].price_frequency
      )
      variant = isVariousRates ? 'variousRates' : 'building'
    } else if (isMultiUnit) {
      variant = 'multiUnit'
    } else if (isRented) {
      variant = 'rented'
    }

    if (isCardLocked) {
      variant = 'locked'
    }

    const price = getBuildingPrice(listings, variant, t)
    const bedsBaths = getBuildingBedsBaths(listings)
    const sqft = isMultiUnit || listing_count > 1 ? null : listings[0]?.size
    const imageUrls = getListingImages(listing, listings, variant)
    const listingLink = getListingUrl(listing, isFeatured)

    const isVerified = listings.some(
      l => l.verified_state_machine === 'approved'
    )
    const cityRegion = `${listing.city}, ${listing.state}`
    const listingType =
      listing_count > 1
        ? listing.building_name
        : getListingTypeText(
            {
              unitType: listings[0]?.unit_type_txt_id,
              unitScope: listings[0]?.unit_type_scope_txt_id
            },
            t
          )

    const shouldWrapWithExternalLink =
      listingLink && !['building', 'variousRates', 'locked'].includes(variant)

    const frequencyTextMap = {
      monthly: t('Month'),
      weekly: t('Week'),
      daily: t('Day'),
      yearly: t('Year'),
      one_time: t('One-time')
    }

    let period: string | undefined
    const listingPriceFrequency =
      'price_frequency' in listing
        ? (listing as IndividualListing).price_frequency
        : listings[0]?.price_frequency

    if (
      listingPriceFrequency &&
      Object.keys(frequencyTextMap).includes(listingPriceFrequency)
    ) {
      period =
        '/' +
        frequencyTextMap[listingPriceFrequency as keyof typeof frequencyTextMap]
    } else {
      period = ''
    }

    const priceText = formatPrice(price, {
      plusIfNoMax: isMultiUnit,
      period: period
    })

    const firstListing = listing.listings?.[0]

    const isSingleListing =
      listing_count === 1 &&
      variant !== 'locked' &&
      variant !== 'rented' &&
      variant !== 'variousRates'

    const displayPrice =
      variant === 'locked' ? { price: '$****', period: '/month' } : priceText
    let displayDetails = `${formatBedsBaths(bedsBaths, variant, t)} ${
      sqft ? `· ${sqft} ft²` : ''
    }`

    if (isSingleListing) {
      const unitTypeScopeTxtId: string | undefined = listing.listings?.[0]
        ? (listing.listings[0] as any).unit_type_scope_txt_id
        : (listing as any).unit_type_scope_txt_id

      if (unitTypeScopeTxtId === 'SINGLE') {
        displayDetails = t('common.room', 'Room')
      }
    }

    let displayAddress

    if (variant === 'locked') {
      displayAddress = '****'
    } else {
      if (listing.listings?.[0]) {
        displayAddress = isStreetNumberHidden(listing.listings?.[0])
          ? (listing as any).street_name
          : (listing as any).full_street_name
      } else {
        displayAddress = isStreetNumberHidden(listing)
          ? (listing as any).street_name
          : (listing as any).full_street_name
      }
    }

    const displayCityRegion =
      variant === 'locked' ? 'Vancouver · Apartment for Rent' : cityRegion

    const listingId = listing.listings
      ? listing.listings[0].listing_id
      : listing.listing_id

    if ('listings' in listing && listing.listings) {
      if (variant === 'building' || variant === 'variousRates') {
        // Building and variousRates variants should never show special offer badge
        specialOffer = false
      } else {
        specialOffer =
          listing.listings?.[0]?.search_deals?.includes('special_offer') ??
          false
      }
    } else {
      specialOffer =
        (listing as IndividualListing).search_deals?.includes(
          'special_offer'
        ) ?? false
    }

    const hasPriceDrop =
      'listings' in listing && listing.listings
        ? listing.listings?.[0]?.search_deals?.includes('price_drop') ?? false
        : (listing as IndividualListing).search_deals?.includes('price_drop') ??
          false

    const rawPreviousPrice =
      'listings' in listing && listing.listings
        ? firstListing?.previous_price
        : (listing as IndividualListing).previous_price

    // Building cards should not show price drops
    const shouldShowPriceDrop =
      hasPriceDrop && variant !== 'building' && variant !== 'variousRates'

    const previousPrice =
      rawPreviousPrice && shouldShowPriceDrop && hasPriceDrop
        ? parseInt(rawPreviousPrice.toString()).toFixed(0)
        : null

    return {
      imageUrls,
      address: listing.full_street_name,
      cityRegion,
      bedsBaths,
      sqft,
      price: price || { label: 'Error getting price' },
      variant,
      listingType,
      listingLink,
      shouldWrapWithExternalLink,
      unitsAvailable: units_available,
      numberOfListings: listing_count,
      verified: isVerified,
      listingCardDlv,
      displayPrice,
      displayDetails,
      displayAddress,
      displayCityRegion,
      isSingleListing,
      listingId,
      specialOffer,
      previousPrice
    }
  }, [
    listing,
    isFeatured,
    hasSession,
    isAgreementMismatched,
    isAgreementLoading,
    t
  ])
}
